<template>
	<div :class="{ 'is-error': isError }" class="container">
		<NotFoundContent v-if="isError" />
		<div v-else-if="isLoading">
			<p>Loading pack {{ $route.params.pack }}</p>
			<LoadingSpinner />
		</div>
		<div v-else-if="packInfo">
			<Breadcrumbs
				v-if="!isSmallDevice"
				:items="breadcrumb"
				@onBreadcrumbClick="onBreadcrumbClick"
			/>
			<h1 v-html="title" />
			<div ref="card" class="card">
				<div
					:style="{ backgroundColor: packInfo.color }"
					class="top-border"
				/>
				<div class="top" v-html="packTitle" />
				<ul class="levels">
					<template v-for="(level, levelIndex) in packInfo.levels">
						<li :key="level.name">
							<router-link
								:to="{
									name: 'LevelPage',
									params: {
										group: packInfo.group,
										pack: packInfo.name,
										level: level.level,
									},
								}"
								class="link"
							>
								<span
									class="text-wrapper"
									@click="handleClick(level)"
									v-html="levelTitle(level)"
								/>
							</router-link>
						</li>
						<li
							v-if="
								isSmallDevice &&
									getLevelAd(
										levelIndex,
										packInfo.levels.length
									)
							"
							:key="`advertising-${level.name}`"
							class="advertising-container"
						>
							<Advertising
								:stub="
									getLevelAd(
										levelIndex,
										packInfo.levels.length
									)
								"
							/>
						</li>
					</template>
				</ul>
			</div>
		</div>
		<aside v-if="!isError && !isSmallDevice" class="sidebar">
			<Advertising
				:stub="AD_LAYOUT_STUBS.RIGHT_RAIL"
				:style="{ top: `${topElementsHeight + 25}px` }"
				class="advertising--sticky"
			/>
		</aside>
	</div>
</template>

<script>
import { AD_LAYOUT_STUBS } from "@/common/consts";
const Advertising = () =>
	import(
		/* webpackChunkName: "component-advertising" */ "@/components/advertising"
	);
const Breadcrumbs = () =>
	import(
		/* webpackChunkName: "component-breadcrumbs" */ "@/components/breadcrumbs"
	);
const LoadingSpinner = () =>
	import(
		/* webpackChunkName: "component-loading-spinner" */ "@/components/loading-spinner"
	);
import NotFoundContent from "@/pages/not-found/content.vue";

import { getPackInfo } from "@/common/services/search";
import metaInfo from "./meta";
import { mapGetters } from "vuex";

export default {
	name: "PackPage",
	components: {
		Advertising,
		Breadcrumbs,
		LoadingSpinner,
		NotFoundContent,
	},
	data() {
		return {
			AD_LAYOUT_STUBS,
			levelsPerAd: 3,
			ads: [AD_LAYOUT_STUBS.MOB_IN_CONTENT],
			packInfo: null,
			isLoading: false,
			isError: false,
		};
	},
	computed: {
		...mapGetters(["topElementsHeight"]),
		title() {
			return this.$t("WordscapesPackPageH1", {
				PackName: this.packInfo.name,
			});
		},
		packTitle() {
			const maxLevel = 10000;
			const dictKey =
				this.packInfo.max >= maxLevel
					? "WordscapesGroupPageTableHeadingLvl"
					: "WordscapesPackPageTableHeading";

			return this.$t(dictKey, {
				PackName: this.packInfo.name,
				FirstLevel: this.packInfo.min,
				LastLevel: this.packInfo.max,
			});
		},
		breadcrumb() {
			return [
				{
					label: this.$t("BreadcrumbWordscapesGroupPageText", {
						GroupName: this.packInfo.group,
					}),
					url: this.$t("BreadcrumbWordscapesGroupPageUrl", {
						GroupName: this.packInfo.group,
					}),
					type: "Group",
				},
				{
					label: this.$t("BreadcrumbWordscapesPackPageText", {
						PackName: this.packInfo.name,
					}),
				},
			];
		},
	},
	watch: {
		$route: "refreshPage", // call again the method if the route changes
	},
	mounted() {
		this.refreshPage();
	},
	beforeRouteEnter(to, from, next) {
		// WFW-3079: Redirect legacy pack names that contain a number without a separator space (e.g. "/answers/master/valley2/" -> "/answers/master/valley 2/")
		let pack = to.params.pack;
		if (pack[pack.length - 1] >= "0" && pack[pack.length - 1] <= "9") {
			let match = /^(\D+)(\d+)$/.exec(pack); // Split the text part (non digit "\D") from the numeric part (digits "\d").
			if (match) {
				pack = `${match[1].trim()} ${match[2]}`; // Ensure a space between the pack name and the number.
				if (pack !== to.params.pack) {
					next(`/answers/${to.params.group}/${pack}/`);
					return;
				}
			}
		}
		next();
	},
	methods: {
		async refreshPage() {
			this.isError = false;
			this.isLoading = true;
			const packInfo = await getPackInfo(
				this.$route.params.group,
				this.$route.params.pack
			);

			this.isLoading = false;
			if (
				!(
					typeof packInfo === "object" &&
					typeof packInfo.name === "string"
				)
			) {
				this.isError = true;
				if (typeof window.__PRERENDER_INJECTED !== "undefined") {
					this.$nextTick(() => {
						window.document.dispatchEvent(
							new CustomEvent("spa-render-complete")
						);
					});
				}
				return;
			}

			if (packInfo) this.packInfo = packInfo;
		},
		levelTitle(level) {
			return this.$t("WordscapesPackPageTableText", {
				PackName: level.name,
				LevelNumber: level.level,
			});
		},
		getLevelAd(levelIndex, levelCount) {
			// No ads available to show.
			if (!this.ads.length) return null;

			// If it's the correct level, or if it's the last level in the pack, show the ad.
			const adPosition = Math.min(this.levelsPerAd - 1, levelCount - 1);
			if (levelIndex === adPosition) return this.ads[0];

			return null;
		},
		handleClick(level) {
			this.$store.dispatch("trackEvent", {
				eventCategory: "Pack Table",
				eventAction: `Click on Pack table row ${this.levelTitle(
					level
				)}`,
			});
		},
		onBreadcrumbClick(item) {
			const eventAction = `Click on ${item.type} breadcrumb ${this.$t(
				`BreadcrumbWordscapes${item.type}PageText`,
				{
					[`${item.type}Name`]: item.label,
				}
			)}`;

			this.$store.dispatch("trackEvent", {
				eventCategory: "Breadcrumb",
				eventAction,
			});
		},
	},
	metaInfo,
};
</script>

<style lang="scss" src="./styles.scss" scoped></style>
